import React from 'react';
import PropTypes from 'prop-types';
import SystemStatusTab from './SystemStatusTab';
import bulletIcon from '../../common/Images/arrow_bullet.png';
import { SYSTEM_STATUS_ACTIONS, SYSTEM_STATUS_TABS_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import DCStatusRow from '../DCStatusRow';
import NodeStatusRow from '../NodeStatusRow';
import PushServiceStatusRow from '../PushServiceStatusRow';
import AdConnectStatusHeaderRow from '../AdConnectStatusHeaderRow';
import AdminConsoleStatusRow from '../AdminConsoleStatusRow';
import FiltersMenu from '../../Controls/Filters/FiltersMenu';
import { isSystemTabHiddenBySystemMode } from '../../SystemSettings/common/utils';

export const SYSTEM_STATUS_TABS = {
    NODES: 'Silverfort Nodes',
    DCS: 'Domain Controllers',
    PUSH_SERVICES: 'Desktop Messaging Services',
    AD_CONNECT: 'Ad connect',
    ADMIN_CONSOLE: 'Admin Console'
};

const systemStatusTabs = (props) => {
    let tabData = null;
    switch (props.selectedTab) {
        case SYSTEM_STATUS_TABS.NODES:
            tabData = props.nodes;
            break;
        case SYSTEM_STATUS_TABS.DCS:
            if (props.towerSync) {
                tabData = props.dcs;
            } else {
                tabData = (
                    <div className="noADSync">
                        <span>Perform active directory sync to see configured Domain Controllers</span>
                    </div>
                );
            }
            break;
        case SYSTEM_STATUS_TABS.PUSH_SERVICES:
            tabData = props.pushServices;
            break;
        case SYSTEM_STATUS_TABS.AD_CONNECT:
            tabData = props.adConnecters;
            break;
        case SYSTEM_STATUS_TABS.ADMIN_CONSOLE:
            tabData = props.adminConsoleRows;
            break;
        default:
            tabData = null;
            break;
    }

    const systemModeTabsKeys = Object.keys(SYSTEM_STATUS_TABS).filter((tab) => {
        const isHidden = isSystemTabHiddenBySystemMode(tab, props.systemMode, props.isCloudOnly);

        if (tab === SYSTEM_STATUS_TABS_TYPES.PUSH_SERVICES) return !isHidden && props.isMfaOrPasEnabledByLicense;
        return !isHidden;
    });

    const tabs = systemModeTabsKeys.map((tabKey) => {
        const baseTabTitle = SYSTEM_STATUS_TABS[tabKey];
        const tabIsSelected = baseTabTitle === props.selectedTab;
        let fullTabTitle = baseTabTitle;
        let showErrorDot = false;
        let showWarningDot = false;

        if (tabKey === SYSTEM_STATUS_TABS_TYPES.NODES) {
            fullTabTitle += ` (${props.activeNodes}/${props.nodes.length})`;
            showWarningDot = props.showNodesWarningDot;
        } else if (tabKey === SYSTEM_STATUS_TABS_TYPES.DCS) {
            fullTabTitle += ` (${props.activeDcs}/${props.dcs.length})`;
            showErrorDot = props.showErrorDot;
        } else if (tabKey === SYSTEM_STATUS_TABS_TYPES.PUSH_SERVICES) {
            if (!props.isAryaEnabled) {
                return null;
            }
            fullTabTitle += ` (${props.activePushServices}/${props.pushServices.length})`;
            showErrorDot = props.showPushServicesErrorDot;
        } else if (tabKey === SYSTEM_STATUS_TABS_TYPES.AD_CONNECT) {
            fullTabTitle += ` (${props.activeAdConnecters}/${props.adConnecters.length})`;
        } else if (tabKey === SYSTEM_STATUS_TABS_TYPES.ADMIN_CONSOLE) {
            fullTabTitle += ' (1/1)';
        }

        return (
            <SystemStatusTab
                key={tabKey}
                tabKey={tabKey}
                title={fullTabTitle}
                icon={bulletIcon}
                selected={tabIsSelected}
                click={() => props.tabSelected(baseTabTitle)}
                showErrorDot={showErrorDot}
                showWarningDot={showWarningDot}
            />
        );
    });

    if (tabs.length === 0) return null;

    return (
        <React.Fragment>
            <div className="header">
                <div className="tabs">{tabs}</div>
                {props.selectedTab === SYSTEM_STATUS_TABS.NODES && (
                    <div className="actions">
                        <div className="startAllNodesDiv">
                            <button
                                className="startAllNodes"
                                disabled={
                                    !props.actionsButtonsEnabled ||
                                    !props.isEditor ||
                                    !props.actionForAllNodesEnabled(SYSTEM_STATUS_ACTIONS.START)
                                }
                                onClick={() => props.actionForAllNodes(SYSTEM_STATUS_ACTIONS.START)}>
                                Start all nodes
                            </button>
                        </div>

                        <div className="recoverAllNodesDiv">
                            <button
                                className="recoverAllNodes"
                                disabled={
                                    !props.actionsButtonsEnabled ||
                                    !props.isEditor ||
                                    !props.actionForAllNodesEnabled(SYSTEM_STATUS_ACTIONS.RECOVER)
                                }
                                onClick={() => props.actionForAllNodes(SYSTEM_STATUS_ACTIONS.RECOVER)}>
                                Recover all nodes
                            </button>
                        </div>
                    </div>
                )}
                {props.selectedTab === SYSTEM_STATUS_TABS.DCS && (
                    <div className="actions">
                        <div>
                            <button
                                className="recoverAllDcs"
                                disabled={
                                    !props.actionsButtonsEnabled ||
                                    props.allDcsDuringRecover ||
                                    !props.isEditor ||
                                    !props.dcWaitingForRecover
                                }
                                onClick={props.recoverAllDcs}>
                                Recover all DCs
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {props.filters && (
                <FiltersMenu
                    filters={Object.values(props.filters)}
                    onValueChange={props.updateFilter}
                    customStyles={{ marginLeft: '15px' }}
                />
            )}
            <div className="content">
                {props.selectedTab === SYSTEM_STATUS_TABS.DCS && (
                    <DCStatusRow
                        systemMode={props.systemMode}
                        key={`dc_header`}
                        name={'header'}
                        data={{}}
                        isHeader={true}
                        HideExpender={true}
                        checkAllDcs={props.checkAllDcs}
                        isAllDcsSelected={props.isAllDcsSelected}
                        isCloudOnly={props.isCloudOnly}
                    />
                )}
                {props.selectedTab === SYSTEM_STATUS_TABS.NODES && (
                    <NodeStatusRow
                        systemMode={props.systemMode}
                        isCloudOnly={props.isCloudOnly}
                        NodeData={{}}
                        key={`nodes_header`}
                        name={'header'}
                        data={{}}
                        isHeader={true}
                        checkAllNodes={props.checkAllNodes}
                        isAllNodesSelected={props.isAllNodesSelected}
                        hasServiceBus={props.hasServiceBus}
                    />
                )}
                {props.selectedTab === SYSTEM_STATUS_TABS.PUSH_SERVICES && (
                    <PushServiceStatusRow
                        key={`push_header`}
                        name={'header'}
                        data={{}}
                        isHeader={true}
                        HideExpender={true}
                        checkAllPushServices={props.checkAllPushServices}
                        isAllPushServicesSelected={props.isAllPushServicesSelected}
                    />
                )}
                {props.selectedTab === SYSTEM_STATUS_TABS.AD_CONNECT && (
                    <AdConnectStatusHeaderRow
                        key={`ad-connect_header`}
                        checkAllAdConnects={props.checkAllAdConnects}
                        isAllAdConnectsSelected={props.isAllAdConnectsSelected}
                    />
                )}
                {props.selectedTab === SYSTEM_STATUS_TABS.ADMIN_CONSOLE && (
                    <AdminConsoleStatusRow
                        systemMode={props.systemMode}
                        adminConsoleData={{}}
                        key={`admin-console_header`}
                        isHeader={true}
                        checkAdminConsole={props.checkAdminConsole}
                        isAdminConsoleSelected={props.isAdminConsoleSelected}
                        isMfaOrPasEnabledByLicense={props.isMfaOrPasEnabledByLicense}
                        hasServiceBus={props.hasServiceBus}
                    />
                )}
                <div className="tabData">{tabData}</div>
            </div>
        </React.Fragment>
    );
};

systemStatusTabs.propTypes = {
    selectedTab: PropTypes.oneOf(Object.values(SYSTEM_STATUS_TABS)),
    isCloudOnly: PropTypes.bool
};

export default systemStatusTabs;
